/**
 * OverUnderPlayerTeamSelection - Player/Team selection for Over/Under questions
 *
 * This component provides player or team selection using picker buttons
 * and updates the question_metadata.grading.config accordingly.
 */

import { FormikProps } from 'formik';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';
import { PlayerPickerButton } from '@/components/composite/PlayerPickerButton';
import { TeamPickerButton } from '@/components/composite/TeamPickerButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useState } from 'react';

interface OverUnderPlayerTeamSelectionProps {
  formik: FormikProps<QuestionFormValues>;
  selectedTeams?: string[];
  selectedFixtureData?: {
    fixture: {
      homeTeamId?: string;
      awayTeamId?: string;
      homeTeamName?: string;
      awayTeamName?: string;
    };
    fixtureId: string;
  } | null;
  isEditable: boolean;
  restrictedLeagueId?: string;
}

export function OverUnderPlayerTeamSelection({
  formik,
  selectedTeams = [],
  selectedFixtureData,
  isEditable,
  restrictedLeagueId,
}: OverUnderPlayerTeamSelectionProps) {
  const { values, setFieldValue } = formik;

  // Hooks must be called before early return
  const [selectedPlayerData, setSelectedPlayerData] = useState<{
    name: string;
    position?: string;
    id?: string;
  } | null>(null);

  const [selectedTeamData, setSelectedTeamData] = useState<{
    name: string;
    id?: string;
  } | null>(null);

  const isPlayerOverUnder = values.question_type === ('player_over_under' as any);
  const isTeamOverUnder = values.question_type === ('team_over_under' as any);
  const isLegacyOverUnder = values.question_type === 'over_under';

  // Only render for over/under question types
  if (!isPlayerOverUnder && !isTeamOverUnder && !isLegacyOverUnder) {
    return null;
  }

  const config = values.question_metadata?.grading?.config as
    | {
        playerId?: string;
        playerName?: string;
        playerPosition?: string;
        teamId?: string | number;
        teamName?: string;
        teamShortcode?: string;
      }
    | undefined;

  const playerId = config?.playerId;
  const playerName = config?.playerName;
  const playerPosition = config?.playerPosition;
  const teamId = config?.teamId;
  const teamName = config?.teamName;
  // teamShortcode is stored in config but not needed as a variable here

  // Check if this is a feed-based question (generated via Question Coach)
  const isFeedBased = values.options?.some(
    opt => opt.grading_data && (opt.grading_data.playerId || opt.grading_data.teamId)
  );

  const fieldsEditable = isEditable && !isFeedBased;

  const handlePlayerSelect = (selection: {
    playerName: string;
    playerId?: string;
    playerPosition?: string;
    teamId?: string;
    jerseyNo: number;
    teamName: string;
    teamAbbr: string;
    jerseyUrl: string;
    teamLogoUrl: string;
    sport: string;
  }) => {
    setSelectedPlayerData({
      name: selection.playerName,
      position: selection.playerPosition,
      id: selection.playerId,
    });

    // Determine teamId from fixture if available, otherwise use selection.teamId
    let resolvedTeamId: string | undefined = selection.teamId;
    if (selectedFixtureData?.fixture) {
      // Match teamName from selection to fixture teams to get teamId
      if (selectedFixtureData.fixture.homeTeamName === selection.teamName) {
        resolvedTeamId = selectedFixtureData.fixture.homeTeamId;
      } else if (selectedFixtureData.fixture.awayTeamName === selection.teamName) {
        resolvedTeamId = selectedFixtureData.fixture.awayTeamId;
      }
    }

    setFieldValue('question_metadata.grading', {
      ...values.question_metadata?.grading,
      type: 'player_stat_over_under',
      sportccMatchId:
        values.question_metadata?.grading?.sportccMatchId ||
        (values.sportcc_match_id as number) ||
        0, // Required by backend schema
    });

    // Update config with all available data including team info from player selection
    // Target format: teamId, statType, teamName, threshold, playerName, teamLogoUrl, teamShortcode, playerPosition
    setFieldValue('question_metadata.grading.config', {
      ...config,
      playerId: selection.playerId,
      playerName: selection.playerName,
      playerPosition: selection.playerPosition || '',
      // Store team info from player selection (player belongs to a team)
      teamId: resolvedTeamId,
      teamName: selection.teamName,
      teamShortcode: selection.teamAbbr,
      teamLogoUrl: selection.teamLogoUrl,
    });

    // Also update question image if jersey URL is available
    if (selection.jerseyUrl) {
      setFieldValue('question_image_en', selection.jerseyUrl);
      setFieldValue('question_image_fr', selection.jerseyUrl);
    }
  };

  const handleTeamSelect = (selection: {
    teamName: string;
    teamId?: string;
    teamAbbr: string;
    teamLogoUrl: string;
    sport: string;
  }) => {
    setSelectedTeamData({
      name: selection.teamName,
      id: selection.teamId,
    });

    setFieldValue('question_metadata.grading', {
      ...values.question_metadata?.grading,
      type: 'team_stat_over_under',
      sportccMatchId:
        values.question_metadata?.grading?.sportccMatchId ||
        (values.sportcc_match_id as number) ||
        0, // Required by backend schema
    });

    // Update config with all available data
    setFieldValue('question_metadata.grading.config', {
      ...config,
      teamId: selection.teamId,
      teamName: selection.teamName,
      teamLogoUrl: selection.teamLogoUrl,
      teamShortcode: selection.teamAbbr,
    });

    // Also update question image if logo URL is available
    if (selection.teamLogoUrl) {
      setFieldValue('question_image_en', selection.teamLogoUrl);
      setFieldValue('question_image_fr', selection.teamLogoUrl);
    }
  };

  // Display current selection from config
  const displayPlayer =
    selectedPlayerData ||
    (playerName ? { name: playerName, position: playerPosition, id: playerId } : null);
  const displayTeam =
    selectedTeamData || (teamName ? { name: teamName, id: String(teamId) } : null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {isPlayerOverUnder || isLegacyOverUnder ? 'Player Selection' : 'Team Selection'}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {(isPlayerOverUnder || isLegacyOverUnder) && (
          <div className="flex flex-col gap-2">
            <Label>
              Select Player <span className="text-destructive">*</span>
            </Label>
            <div className="flex items-center gap-4">
              <PlayerPickerButton
                selectedTeams={selectedTeams}
                onSelect={handlePlayerSelect}
                fieldLabel="Over/Under Question"
                disabled={!fieldsEditable}
                restrictedLeagueId={restrictedLeagueId}
              />
              {displayPlayer && (
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {displayPlayer.name}
                    {displayPlayer.position && ` (${displayPlayer.position})`}
                  </p>
                  {displayPlayer.id && (
                    <p className="text-xs text-muted-foreground">ID: {displayPlayer.id}</p>
                  )}
                </div>
              )}
            </div>
            {!displayPlayer && !isFeedBased && (
              <p className="text-sm text-muted-foreground">
                Click the button to select a player from the sports feed
              </p>
            )}
          </div>
        )}

        {isTeamOverUnder && (
          <div className="flex flex-col gap-2">
            <Label>
              Select Team <span className="text-destructive">*</span>
            </Label>
            <div className="flex items-center gap-4">
              <TeamPickerButton
                selectedTeams={selectedTeams}
                restrictedLeagueId={restrictedLeagueId}
                onSelect={handleTeamSelect}
                fieldLabel="Over/Under Question"
                disabled={!fieldsEditable}
              />
              {displayTeam && (
                <div className="flex-1">
                  <p className="text-sm font-medium">{displayTeam.name}</p>
                  {displayTeam.id && (
                    <p className="text-xs text-muted-foreground">ID: {displayTeam.id}</p>
                  )}
                </div>
              )}
            </div>
            {!displayTeam && !isFeedBased && (
              <p className="text-sm text-muted-foreground">
                Click the button to select a team from the sports feed
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
